/**
 * Produktfamilien -- Liste und Detail.
 *
 * Der Arbeitsort fuer die auf Familienebene gefuehrten redaktionellen Texte.
 * Bewusst KEIN Familieneditor: Name, Kategorie und Artikelzuordnung sind
 * fachlich nicht zur Bearbeitung freigegeben und werden hier nur gelesen.
 *
 * Die Familienliste kommt aus `meta` -- dieselbe Quelle, aus der sich schon
 * die Filter der Artikelliste speisen. Es entsteht keine zweite Liste und
 * kein zusaetzlicher Endpunkt.
 */
import { useEffect, useMemo, useState } from "react";
import type { ArticleListItem, CategoryItem, FamilyItem, MetaResponse } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { categoryLabel, familyLabel } from "../i18n/terminology.ts";
import { EmptyState, ErrorState, LoadingState, Unknown } from "./States.tsx";
import { RoleBadge } from "./Badges.tsx";
import { FamilyTexts } from "./FamilyTexts.tsx";
import { ProductProperties } from "./ProductProperties.tsx";
import { StructureLifecycle } from "./StructureLifecycle.tsx";
import "./Families.css";

export function Families({ meta, mayWrite, mayRelease, mayWriteProps, mayConfirmProps,
  mayManageFamilies, mayManageCategories, mayDeleteStructures, onStructureChanged }: {
  meta: MetaResponse;
  mayWrite: boolean;
  mayRelease: boolean;
  mayWriteProps: boolean;
  mayConfirmProps: boolean;
  /** `article.write` -- Familien pflegen darf, wer sie anlegen darf. */
  mayManageFamilies: boolean;
  /** `system.manage` -- die Hauptkategorie gliedert den ganzen Katalog. */
  mayManageCategories: boolean;
  /** `article.delete` -- endgueltiges Loeschen, dieselbe Schwelle wie am Artikel. */
  mayDeleteStructures: boolean;
  onStructureChanged: () => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const [openId, setOpenId] = useState<number | null>(null);
  const [openCategory, setOpenCategory] = useState<number | null>(null);
  /*
   * ZWEI EBENEN, EINE SEITE (Lifecycle 001).
   *
   * Hauptkategorie und Produktfamilie gehoeren zusammen -- die eine ordnet
   * die andere. Ein eigener Menuepunkt fuer zehn Kategorien waere ein
   * Bereich fuer eine Liste; hier stehen beide dort, wo man sie sucht.
   */
  const [tab, setTab] = useState<"families" | "categories">("families");

  if (tab === "categories") {
    return (
      <div className={openCategory !== null ? "app__split app__split--detail" : "app__split"}>
        <div className="list families">
          <StrukturReiter tab={tab} onTab={(x) => { setTab(x); setOpenCategory(null); setOpenId(null); }} i18n={i18n} />
          <CategoryList meta={meta} openId={openCategory} onOpen={setOpenCategory} />
        </div>
        {openCategory !== null && meta.categories.some((c) => c.id === openCategory) && (
          <CategoryDetailPanel key={openCategory}
            category={meta.categories.find((c) => c.id === openCategory)!} meta={meta}
            mayManage={mayManageCategories} mayDelete={mayDeleteStructures}
            onChanged={onStructureChanged}
            onDeleted={() => { setOpenCategory(null); onStructureChanged(); }}
            onOpenFamily={(id) => { setTab("families"); setOpenCategory(null); setOpenId(id); }}
            onClose={() => setOpenCategory(null)} />
        )}
      </div>
    );
  }

  return (
    /*
     * DERSELBE DETAILMODUS WIE ÜBERALL -- sonst ist das Detail ein Strich.
     *
     * `.app__split` ist eine Flex-Zeile, in der die Liste mit `flex: 1`
     * allen Platz beansprucht. Das Detail traegt `container-type:
     * inline-size`; seine Inhaltsbreite rechnet damit als 0, und ohne
     * eigenen Wachstumsanteil bleibt es dort. Gemessen am 12.09.2026: 1 px
     * breit, mit vollstaendigem Inhalt, unsichtbar. Derselbe Befund wie
     * zuvor bei den Produktmodellen -- dieselbe Zeile behebt ihn.
     */
    <div className={openId !== null ? "app__split app__split--detail" : "app__split"}>
      <FamilyList meta={meta} openId={openId} onOpen={setOpenId}
        reiter={<StrukturReiter tab={tab} onTab={(x) => { setTab(x); setOpenId(null); }} i18n={i18n} />} />
      {openId !== null && meta.families.some((f) => f.id === openId) && (
        <FamilyDetailPanel
          key={openId} family={meta.families.find((f) => f.id === openId)!} meta={meta}
          mayWrite={mayWrite} mayRelease={mayRelease}
          mayWriteProps={mayWriteProps} mayConfirmProps={mayConfirmProps}
          mayManage={mayManageFamilies} mayDelete={mayDeleteStructures}
          onChanged={onStructureChanged}
          onDeleted={() => { setOpenId(null); onStructureChanged(); }}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

/** Die beiden Ebenen als Reiter -- dasselbe Muster wie auf der Modellseite. */
function StrukturReiter({ tab, onTab, i18n }: {
  tab: "families" | "categories"; onTab: (t: "families" | "categories") => void; i18n: I18n;
}): React.JSX.Element {
  return (
    <div className="subnav families__reiter" role="tablist">
      {(["families", "categories"] as const).map((x) => (
        <button key={x} role="tab" aria-selected={tab === x}
          className={tab === x ? "subnav__item subnav__item--on" : "subnav__item"}
          data-testid={`sl-reiter-${x}`}
          onClick={() => onTab(x)}>{i18n.t(x === "families" ? "families.title" : "sl.categories")}</button>
      ))}
    </div>
  );
}

/** Aktiv, stillgelegt oder im Papierkorb -- eine Marke, immer dieselbe. */
function Zustand({ item, i18n }: { item: { archived?: boolean; trashed?: boolean }; i18n: I18n }): React.JSX.Element | null {
  if (item.trashed) return <span className="sl__marke sl__marke--papierkorb" data-testid="sl-marke">{i18n.t("sl.stateTrashed")}</span>;
  if (item.archived) return <span className="sl__marke sl__marke--archiv" data-testid="sl-marke">{i18n.t("sl.stateArchived")}</span>;
  return null;
}

/** Die Hauptkategorien mit ihrem Zustand. */
function CategoryList({ meta, openId, onOpen }: {
  meta: MetaResponse; openId: number | null; onOpen: (id: number) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [zustand, setZustand] = useState<"aktiv" | "archiviert" | "papierkorb" | "alle">("aktiv");
  const gezeigt = meta.categories.filter((c) => zustand === "alle"
    || (zustand === "aktiv" && !c.archived && !c.trashed)
    || (zustand === "archiviert" && c.archived === true)
    || (zustand === "papierkorb" && c.trashed === true));
  return (<>
    <div className="list__toolbar">
      <select className="list__select" data-testid="sl-zustandsfilter" value={zustand}
              aria-label={t("sl.stateFilter")}
              onChange={(e) => setZustand(e.target.value as typeof zustand)}>
        <option value="aktiv">{t("sl.filterActive")}</option>
        <option value="archiviert">{t("sl.filterArchived")}</option>
        <option value="papierkorb">{t("sl.filterTrashed")}</option>
        <option value="alle">{t("sl.filterAll")}</option>
      </select>
    </div>
    <div className="list__meta"><span><strong>{gezeigt.length}</strong> {t("sl.categories")}</span></div>
    {gezeigt.length === 0 ? <EmptyState title={t("sl.emptyTitle")} hint={t("sl.emptyHint")} /> : (
      <div className="list__table">
        <table>
          <thead><tr><th>{t("sl.category")}</th><th>{t("families.code")}</th>
            <th className="list__num">{t("families.colFamilies")}</th>
            <th className="list__num">{t("families.colArticles")}</th></tr></thead>
          <tbody>
            {gezeigt.map((c) => (
              <tr key={c.id} data-testid="sl-kategoriezeile" className={openId === c.id ? "list__row--open" : undefined}
                  onClick={() => onOpen(c.id)}>
                <td>{categoryLabel(i18n, c.code, c.name)} <Zustand item={c} i18n={i18n} /></td>
                <td><code>{c.code}</code></td>
                <td className="list__num">{meta.families.filter((f) => f.categoryId === c.id).length}</td>
                <td className="list__num">{c.articleCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>);
}

/** Das Detail einer Hauptkategorie -- Zahlen, Familien, Lebenszyklus. */
function CategoryDetailPanel({ category, meta, mayManage, mayDelete, onChanged, onDeleted, onClose,
  onOpenFamily }: {
  category: CategoryItem; meta: MetaResponse; mayManage: boolean; mayDelete: boolean;
  onChanged: () => void; onDeleted: () => void; onClose: () => void;
  /** Eine Familie dieser Kategorie oeffnen -- der Weg dorthin. */
  onOpenFamily: (id: number) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const familien = meta.families.filter((f) => f.categoryId === category.id);
  return (
    <aside className="detail" aria-label={t("sl.categoryDetailAria")}>
      <div className="detail__head">
        <h2 className="detail__title">{categoryLabel(i18n, category.code, category.name)}</h2>
        <button className="btn" onClick={onClose}>{t("common.close")}</button>
      </div>
      <div className="detail__body">
        <Field label={t("families.code")}><code>{category.code}</code> <Zustand item={category} i18n={i18n} /></Field>
        <Field label={t("families.colFamilies")}>{familien.length}</Field>
        <Field label={t("families.colArticles")}>{category.articleCount}</Field>
        {/* KEINE SACKGASSE (003 V2): die Familien der Kategorie sind der
            Weg zu ihnen, nicht nur eine Aufzaehlung. */}
        {familien.length > 0 && (
          <ul className="families__articles" data-testid="sl-kategoriefamilien">
            {familien.map((f) => (
              <li key={f.id} className="families__article">
                <button type="button" className="models__link families__familielink"
                  data-testid="sl-familielink" data-code={f.code}
                  onClick={() => onOpenFamily(f.id)}>
                  <span className="families__artnum"><code>{f.code}</code> <Zustand item={f} i18n={i18n} /></span>
                  <span className="families__artdesc">{familyLabel(i18n, f.code, f.name)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <StructureLifecycle kind="category" id={category.id} code={category.code} name={category.name}
          mayManage={mayManage} mayDelete={mayDelete} onChanged={onChanged} onDeleted={onDeleted} />
      </div>
    </aside>
  );
}

function FamilyList({ meta, openId, onOpen, reiter }: {
  meta: MetaResponse; openId: number | null; onOpen: (id: number) => void; reiter: React.ReactNode;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [zustand, setZustand] = useState<"aktiv" | "archiviert" | "papierkorb" | "alle">("aktiv");

  /**
   * Gesucht wird ueber Name, Code und historische Bezeichnung. Der
   * Legacy-Alias ist kein zweiter kanonischer Name, aber wer ihn im Kopf hat,
   * soll die Familie damit finden koennen.
   */
  const families = useMemo(() => {
    const q = search.trim().toLowerCase();
    return meta.families.filter((f) => {
      if (zustand === "aktiv" && (f.archived === true || f.trashed === true)) return false;
      if (zustand === "archiviert" && f.archived !== true) return false;
      if (zustand === "papierkorb" && f.trashed !== true) return false;
      if (categoryId && f.categoryId !== categoryId) return false;
      if (!q) return true;
      // Auch der uebersetzte Anzeigename ist durchsuchbar: wer in der
      // englischen Ansicht nach "Drivers" sucht, soll `Treiber` finden.
      return [f.name, f.code, f.legacyAlias ?? "", familyLabel(i18n, f.code, f.name)]
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [meta.families, search, categoryId, zustand, i18n]);

  return (
    <div className="list families">
      {reiter}
      <div className="list__toolbar">
        <input
          className="list__search"
          placeholder={t("families.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t("families.search")}
        />
        <select className="list__select" value={categoryId ?? ""} aria-label={t("list.category")}
                onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : undefined)}>
          <option value="">{t("list.allCategories")}</option>
          {meta.categories.map((c) => (
            <option key={c.id} value={c.id}>{categoryLabel(i18n, c.code, c.name)}</option>
          ))}
        </select>
        <select className="list__select" data-testid="sl-zustandsfilter" value={zustand}
                aria-label={t("sl.stateFilter")}
                onChange={(e) => setZustand(e.target.value as typeof zustand)}>
          <option value="aktiv">{t("sl.filterActive")}</option>
          <option value="archiviert">{t("sl.filterArchived")}</option>
          <option value="papierkorb">{t("sl.filterTrashed")}</option>
          <option value="alle">{t("sl.filterAll")}</option>
        </select>
        {(search || categoryId) && (
          <button className="btn list__reset"
            onClick={() => { setSearch(""); setCategoryId(undefined); }}>
            {t("list.resetFilters")}
          </button>
        )}
      </div>

      <div className="list__meta">
        <span>
          <strong>{families.length}</strong> {t("families.countLabel")}
          {families.length !== meta.families.length && <> {t("list.ofTotal", { total: meta.families.length })}</>}
        </span>
      </div>

      {families.length === 0 ? (
        <EmptyState title={t("families.emptyTitle")} hint={t("families.emptyHint")} />
      ) : (
        <div className="list__table">
          <table>
            <thead>
              <tr>
                <th>{t("families.colName")}</th>
                <th>{t("families.colCategory")}</th>
                <th className="families__num">{t("families.colArticles")}</th>
              </tr>
            </thead>
            <tbody>
              {families.map((f) => (
                <tr key={f.id} tabIndex={0}
                    className={openId === f.id ? "families__row--open" : ""}
                    aria-current={openId === f.id ? "true" : undefined}
                    onClick={() => onOpen(f.id)}
                    onKeyDown={(e) => { if (e.key === "Enter") onOpen(f.id); }}>
                  <td className="cell-number">{familyLabel(i18n, f.code, f.name)}</td>
                  <td className="cell-cat">{categoryLabel(i18n, ...category(meta, f.categoryId))}</td>
                  <td className="families__num">{f.articleCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Code und gespeicherter Name einer Produktkategorie -- beides fuer die Anzeige. */
const category = (meta: MetaResponse, id: number): [string, string] => {
  const c = meta.categories.find((x) => x.id === id);
  return c ? [c.code, c.name] : ["", "—"];
};

function FamilyDetailPanel({ family, meta, mayWrite, mayRelease, mayWriteProps, mayConfirmProps,
  mayManage, mayDelete, onChanged, onDeleted, onClose }: {
  family: FamilyItem; meta: MetaResponse;
  mayWrite: boolean; mayRelease: boolean;
  mayWriteProps: boolean; mayConfirmProps: boolean;
  mayManage: boolean; mayDelete: boolean;
  onChanged: () => void; onDeleted: () => void; onClose: () => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [articles, setArticles] = useState<readonly ArticleListItem[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setArticles(null); setError(null);
    // Dieselbe Artikelabfrage wie in der Artikelliste -- kein eigener
    // Endpunkt fuer denselben Zweck.
    api.articles({ familyId: family.id, pageSize: 500, sort: "article_number" })
      .then((r) => { if (!cancelled) setArticles(r.items); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [family.id]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const primary = articles?.filter((a) => a.families.find((f) => f.id === family.id)?.isPrimary).length ?? 0;
  const secondary = (articles?.length ?? 0) - primary;

  return (
    <aside className="detail" aria-label={t("families.detailAria")}>
      <div className="detail__head">
        {/* Der Familienname ist ein fachlicher Datenwert. */}
        <h2 className="detail__title">{familyLabel(i18n, family.code, family.name)}</h2>
        <button className="btn" onClick={onClose} aria-label={t("common.close")}>{t("common.close")}</button>
      </div>

      <div className="detail__body">
        <Field label={t("families.colCategory")}>
          {categoryLabel(i18n, ...category(meta, family.categoryId))}
        </Field>
        <Field label={t("families.code")}><code>{family.code}</code> <Zustand item={family} i18n={i18n} /></Field>
        <Field label={t("families.legacyAlias")}>
          {family.legacyAlias ?? <Unknown />}
        </Field>
        <Field label={t("families.colArticles")}>
          {family.articleCount}
          {secondary > 0 && (
            <span className="families__split">
              {" "}{t("families.primarySplit", { primary, secondary })}
            </span>
          )}
        </Field>

        <h3 className="detail__section">{t("families.articles")}</h3>
        {error !== null && <ErrorState message={errorText(i18n, error)} />}
        {!articles && error === null && <LoadingState />}
        {articles && articles.length === 0 && (
          <p className="families__none">{t("families.noArticles")}</p>
        )}
        {articles && articles.length > 0 && (
          <ul className="families__articles">
            {articles.map((a) => {
              const link = a.families.find((f) => f.id === family.id);
              return (
                <li key={a.id} className="families__article">
                  <div className="families__artnum">
                    <code>{a.articleNumber}</code>
                    {/* Ein Artikel kann mehreren Familien angehoeren. Sichtbar
                        machen, ob DIESE Familie die primaere ist. */}
                    {link && !link.isPrimary && (
                      <span className="families__secondary" title={t("families.secondaryHint")}>
                        {t("families.secondary")}
                      </span>
                    )}
                    <span className="families__artroles">
                      {a.roles.map((r) => (
                        <RoleBadge key={r.code} label={i18n.tCode("articleRole", r.code, r.name)} />
                      ))}
                    </span>
                  </div>
                  <div className="families__artdesc">{a.shortDescription ?? <Unknown />}</div>
                </li>
              );
            })}
          </ul>
        )}

        <ProductProperties subject={{ kind: "family", id: family.id }} meta={meta}
          mayWrite={mayWriteProps} mayConfirm={mayConfirmProps} />

        <FamilyTexts familyId={family.id} meta={meta} mayWrite={mayWrite} mayRelease={mayRelease} />

        <StructureLifecycle kind="family" id={family.id} code={family.code} name={family.name}
          mayManage={mayManage} mayDelete={mayDelete} onChanged={onChanged} onDeleted={onDeleted} />
      </div>
    </aside>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="detail__field">
      <div className="detail__label">{label}</div>
      <div className="detail__value">{children}</div>
    </div>
  );
}
